import type { InspirationInfo } from "../auth/api-client";

type InspirationModalProps = {
  readonly open: boolean;
  readonly noun: "lesson" | "course";
  readonly info: InspirationInfo | null;
  readonly loading?: boolean;
  readonly upgrading?: boolean;
  readonly onClose: () => void;
  readonly onUpgrade?: () => void;
  readonly onFindAuthor?: (email: string) => void;
};

export function InspirationModal({
  open,
  noun,
  info,
  loading,
  upgrading,
  onClose,
  onUpgrade,
  onFindAuthor,
}: InspirationModalProps) {
  if (!open) return null;

  return (
    <div className="ccs-modal-overlay" role="dialog" aria-modal="true" aria-label="Inspiration details">
      <div className="teacher-modal-card">
        <h2 className="teacher-modal-title">Inspiration</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : !info ? (
          <p className="muted">This {noun} no longer tracks a community source.</p>
        ) : (
          <>
            <p className="muted">
              Inspired by <strong>{info.sourceTitle}</strong>
              {info.authorName ? (
                <>
                  {" "}
                  by <strong>{info.authorName}</strong>
                </>
              ) : null}
              .
            </p>
            {info.detached ? (
              <p className="muted small">
                You have edited this {noun}, so it no longer receives updates. The inspiration link stays for
                reference.
              </p>
            ) : info.updateAvailable ? (
              <>
                <p className="small">
                  A newer version is available (v{info.communityVersion} vs your v
                  {info.sourceVersion ?? 1}). What changed:
                </p>
                <ul className="teacher-changelog-list">
                  {info.changelog.length === 0 ? (
                    <li className="muted small">No notes provided.</li>
                  ) : (
                    info.changelog.map((c) => (
                      <li key={c.version} className="small">
                        <strong>v{c.version}</strong>
                        {c.changelog ? `: ${c.changelog}` : ": (no note)"}
                      </li>
                    ))
                  )}
                </ul>
              </>
            ) : (
              <p className="muted small">You are on the latest version.</p>
            )}
          </>
        )}
        <div className="teacher-modal-actions">
          {info?.authorEmail && onFindAuthor ? (
            <button
              type="button"
              className="btn ghost tight"
              onClick={() => onFindAuthor(info.authorEmail as string)}
            >
              More from this author
            </button>
          ) : null}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost" onClick={onClose}>
            Close
          </button>
          {info && !info.detached && info.updateAvailable && onUpgrade ? (
            <button type="button" className="btn primary" disabled={upgrading} onClick={onUpgrade}>
              {upgrading ? "Upgrading…" : "Upgrade to latest"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
